/* eslint-disable jsx-a11y/aria-activedescendant-has-tabindex */
/* eslint-disable react/prop-types */
/**
 * w3 suggests the menu should have tabIndex -1, not 0
 * https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 */

import {CSSObject, forBrands, Theme} from "@core-ui/react-stitch";
import React, {forwardRef} from "react";
import {DropdownStyleFn, MenuProps, MenuRef} from "../types";

const getMenuInverseThemeStyles = (
  theme: Theme,
  menuIsOpen: boolean | undefined
) => ({
  backgroundColor: theme.color.gray80,
  border: menuIsOpen ? `1px solid ${theme.color.inverse.b1}` : "none",
  color: theme.color.inverse.b1,
  boxShadow: "none",
  boxSizing: "border-box",
  textAlign: "center",
});

const getMenuPdpRedesignThemeStyles = (
  theme: Theme,
  menuIsOpen: boolean | undefined
) => 
  forBrands(theme, {
    br: {
      backgroundColor: "#F6F4EB",
      border: menuIsOpen ? `1px solid ${theme.color.b2}` : "none",
      color: theme.color.gray80,
      boxShadow: "none",
      boxSizing: "border-box",
    },
    brfs: {
      backgroundColor: "#F6F4EB",
      border: menuIsOpen ? `1px solid ${theme.color.b2}` : "none",
      color: theme.color.gray80,
      boxShadow: "none",
      boxSizing: "border-box",
    },
  }) as CSSObject;


const menuCSS: DropdownStyleFn = ({
  theme,
  menuIsOpen,
  inverse,
  enabledFeatures,
}) => {
  const showPdpRedesign =
    inverse !== true &&
    (enabledFeatures?.["cat-br-redesign"] ||
      enabledFeatures?.["pdp-redesign-2022"]);

  return {
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
    overflowY: "auto",
    height: menuIsOpen ? "auto" : 0,
    maxHeight: menuIsOpen ? "200px" : 0,
    position: "absolute",
    width: "100%",
    zIndex: menuIsOpen ? 10 : "auto",
    transition: "max-height 0.25s ease-in-out",
    backgroundColor: theme.color.wh,
    "&:focus": {
      outline: "none",
    },
    ...(inverse ? getMenuInverseThemeStyles(theme, menuIsOpen) : {}),
    ...(showPdpRedesign
      ? getMenuPdpRedesignThemeStyles(theme, menuIsOpen)
      : {}),
  };
};



const Menu = forwardRef<MenuRef, MenuProps>(
  (
    {
      bemBuilder,
      getStyles,
      highlightedIndex,
      menuIsOpen,
      onKeyDown,
      children,
      inverse,
      enabledFeatures,
    },
    ref
  ) => (
    <ul
      ref={ref}
      aria-activedescendant={
        menuIsOpen ? `dropdown-option${highlightedIndex}` : undefined
      }
      className={bemBuilder("menu")}
      css={(theme) =>
        getStyles(
          "Menu",
          menuCSS({theme, menuIsOpen, inverse, enabledFeatures}),
          {
            theme,
            menuIsOpen,
          }
        )
      }
      onKeyDown={onKeyDown}
      role="listbox"
      tabIndex={-1}
    >
      {children}
    </ul>
  )
);

Menu.displayName = "Menu";

export default Menu;
